import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';

import analytics, {
  analyticsMetadataPropTypes,
} from 'site-react/core/Analytics';

/**
 * An outbound link, ie. one that leads to a domain that isn't hosted in this project.
 * If you are linking to something within this project, please use next/link.
 * Adds link tracking, with a slight delay to increase the likelihood of our
 * tracking events reaching Amplitude/Google Analytics/Segment.
 */
const OutboundLink = ({ analyticsMetadata, href, ...props }) => {
  // trackLink isn't a reversible call. We need the current version of
  // analyticsMetadata to be available to the first version of trackLink; a ref
  // gives us a mutable `.current` value that can _always_ hold the latest
  // value of analyticsMetadata
  const analyticsMetadataRef = useRef(analyticsMetadata);
  const linkElement = useRef(null);

  useEffect(() => {
    analyticsMetadataRef.current = { href, ...analyticsMetadata };
  }, [analyticsMetadata, href]);

  useEffect(() => {
    if (linkElement.current !== null) {
      analytics.trackLink(
        linkElement.current,
        'Outbound link clicked',
        () => analyticsMetadataRef.current,
        { sendPageProperties: true },
      );
    }
  }, [analyticsMetadataRef, linkElement]);

  // OutboundLink is meant to be a normal <a> element, with analytics. Since the
  // actual children are defined elsewhere, our eslint config gets tripped up.
  // eslint-disable-next-line jsx-a11y/anchor-has-content
  return <a href={href} ref={linkElement} {...props} />;
};

OutboundLink.defaultProps = {
  analyticsMetadata: {},
};

OutboundLink.propTypes = {
  /**
   * Additional metadata that we want to attach to the analytics event on click.
   *
   * Where possible, use existing properties to convey your metadata. In order
   * to maintain consistency across our events, any new properties should be
   * added to this shape.
   *
   * All properties are optional.
   */
  analyticsMetadata: analyticsMetadataPropTypes,

  /**
   * Destination for this link
   */
  href: PropTypes.string.isRequired,
};

export default OutboundLink;
